import { Check } from 'lucide-react';
import { Page, PageHeader } from '@/features/common/PageHeader';
import {
  THEMES,
  THEME_NAMES,
  useTheme,
  type PrimaryStyle,
  type ThemeName,
} from '@/features/theme';
import { cn } from '@/lib/utils';

/**
 * Full app-theme picker — the desktop counterpart to mobile's
 * `app/settings/theme.tsx`. The topbar dropdown is the quick path;
 * this screen has room for the full per-palette mini-mock + the
 * primary-button style picker side-by-side, so a user evaluating
 * "which theme actually looks right for my brand" gets a richer
 * preview before committing.
 */
export function AppThemeScreen() {
  const { theme, primaryStyle, setTheme, setPrimaryStyle } = useTheme();

  return (
    <Page>
      <PageHeader
        title="App theme"
        description="Pick a palette for the app, plus how primary buttons (Send / Save / Mark paid) read across every screen."
      />

      <section className="mb-8">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Primary buttons
        </h2>
        <p className="mb-3 max-w-2xl text-sm text-muted-foreground">
          How Send / Save / Mark paid look across the app. Switching is
          instant — the dock, modals, and CTAs update everywhere.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <PrimaryStyleCard
            id="ink"
            label="Ink"
            description="Restrained, editorial. Buttons fill with the theme's ink colour."
            previewBg={THEMES[theme].preview.ink}
            previewFg={THEMES[theme].preview.surface}
            active={primaryStyle === 'ink'}
            onSelect={() => setPrimaryStyle('ink')}
          />
          <PrimaryStyleCard
            id="accent"
            label="Accent"
            description="Brand-forward. Buttons take on the palette's accent colour."
            previewBg={THEMES[theme].preview.accent}
            previewFg="#FFFFFF"
            active={primaryStyle === 'accent'}
            onSelect={() => setPrimaryStyle('accent')}
          />
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Palette
        </h2>
        <p className="mb-3 max-w-2xl text-sm text-muted-foreground">
          Pick the palette that feels right for your brand. All five are
          designed for readable financial data — the difference is mood,
          not contrast.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {THEME_NAMES.map((name) => (
            <ThemeCard
              key={name}
              name={name}
              active={theme === name}
              onSelect={() => setTheme(name)}
            />
          ))}
        </div>
      </section>
    </Page>
  );
}

function PrimaryStyleCard({
  id,
  label,
  description,
  previewBg,
  previewFg,
  active,
  onSelect,
}: {
  id: PrimaryStyle;
  label: string;
  description: string;
  previewBg: string;
  previewFg: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      data-style={id}
      className={cn(
        'group flex items-center gap-4 rounded-xl border bg-card p-4 text-left transition-colors',
        active ? 'border-foreground/80' : 'border-line hover:border-line-strong',
      )}
    >
      <div
        className="flex size-16 shrink-0 items-center justify-center rounded-lg"
        style={{ background: 'var(--surface-alt)' }}
      >
        <span
          className="rounded-md px-3 py-1.5 text-[11px] font-semibold"
          style={{ background: previewBg, color: previewFg }}
        >
          Send
        </span>
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-foreground">{label}</div>
        <div className="text-xs leading-snug text-muted-foreground">{description}</div>
      </div>
      {active ? (
        <span
          aria-hidden
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-foreground"
        >
          <Check className="size-4 text-background" strokeWidth={2.5} />
        </span>
      ) : (
        <span
          aria-hidden
          className="block size-7 shrink-0 rounded-full border border-line"
        />
      )}
    </button>
  );
}

function ThemeCard({
  name,
  active,
  onSelect,
}: {
  name: ThemeName;
  active: boolean;
  onSelect: () => void;
}) {
  const meta = THEMES[name];
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        'group flex flex-col rounded-2xl border bg-card text-left transition-colors',
        active ? 'border-foreground/80 shadow-sm' : 'border-line hover:border-line-strong',
      )}
    >
      {/* Mini preview — a paper-on-paper composition painted with the
          candidate palette's tokens, so the user can see what they're
          picking before applying it. Independent of the active theme. */}
      <div
        className="relative h-32 w-full overflow-hidden rounded-t-2xl"
        style={{ background: meta.preview.bg }}
      >
        <div
          className="absolute inset-3 flex flex-col gap-2 rounded-lg p-3"
          style={{ background: meta.preview.surface }}
        >
          <div
            className="h-2 w-1/3 rounded-full"
            style={{ background: meta.preview.ink }}
          />
          <div
            className="h-1.5 w-2/3 rounded-full"
            style={{ background: 'rgba(0,0,0,0.10)' }}
          />
          <div className="mt-auto flex items-end justify-between gap-2">
            <span
              className="rounded-md px-3 py-1 text-[10px] font-semibold"
              style={{ background: meta.preview.ink, color: meta.preview.surface }}
            >
              Send
            </span>
            <span
              className="size-5 rounded-md"
              style={{ background: meta.preview.accent }}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 items-start justify-between gap-3 px-4 py-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">{meta.label}</div>
          <div className="mt-0.5 text-xs leading-snug text-muted-foreground">
            {meta.description}
          </div>
        </div>
        {active ? (
          <span
            aria-hidden
            className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground"
          >
            <Check className="size-3.5 text-background" strokeWidth={2.5} />
          </span>
        ) : (
          <span
            aria-hidden
            className="mt-0.5 block size-6 shrink-0 rounded-full border border-line"
          />
        )}
      </div>
    </button>
  );
}
